/**
 * BannerConsent - Bandeau de consentement Loi 25
 *
 * Affiche un bandeau en bas de page si l'utilisateur n'a pas accepté la dernière version
 * de la politique de confidentialité. Lien vers www.profiremanager.com/confidentialite.
 *
 * Version actuelle : POLICY_VERSION (à incrémenter quand le texte change).
 */

import React, { useEffect, useState } from 'react';
import { Shield, X } from 'lucide-react';
import { apiGet, apiPost } from '../utils/api';
import { useTranslation } from 'react-i18next';

// Version actuelle de la politique de confidentialité
// Incrémenter cette valeur quand la politique change (re-prompt utilisateurs)
const POLICY_VERSION = '2026-02-08';
const POLICY_URL = 'https://www.profiremanager.com/confidentialite';

const BannerConsent = ({ tenantSlug, currentUser }) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!tenantSlug || !currentUser?.id) return;
    let cancelled = false;
    const check = async () => {
      try {
        const latest = await apiGet(tenantSlug, '/me/consent/latest');
        // undefined = session expirée (apiCall redirige vers la connexion) : ne rien afficher
        if (cancelled || latest === undefined) return;
        // Afficher si pas de consentement OU version différente
        if (!latest || !latest.version || latest.version !== POLICY_VERSION) {
          setShow(true);
        }
      } catch (_e) {
        // Silencieux : si endpoint inaccessible, ne pas spammer l'utilisateur
      }
    };
    check();
    return () => { cancelled = true; };
  }, [tenantSlug, currentUser?.id]);

  const handleAccept = async () => {
    try {
      setBusy(true);
      await apiPost(tenantSlug, '/me/consent', {
        version: POLICY_VERSION,
        accepte: true,
      });
      setShow(false);
    } catch (e) {
      // En cas d'erreur, on ferme quand même pour ne pas bloquer l'utilisateur
      setShow(false);
    } finally {
      setBusy(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900 text-white shadow-2xl border-t-2 border-blue-500"
      data-testid="banner-consent"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col md:flex-row items-start md:items-center gap-3">
        <Shield className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5 md:mt-0" />
        <div className="flex-1 text-sm">
          <strong className="block md:inline">{t('bannerConsent.politiqueDeConfidentialite')}</strong>{' '}
          {t('bannerConsent.conformementALaLoi25Quebec', { v: ' ' })}
          <a
            href={POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-blue-300 hover:text-blue-200"
            data-testid="banner-consent-policy-link"
          >
            {t('bannerConsent.lireLaPolitiqueComplete')}
          </a>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleAccept}
            disabled={busy}
            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded transition-colors disabled:opacity-50"
            data-testid="banner-consent-accept"
          >
            {busy ? t('bannerConsent.enregistrement') : t('bannerConsent.jAccepte')}
          </button>
          <button
            onClick={() => setShow(false)}
            className="text-slate-400 hover:text-white p-1"
            title={t('bannerConsent.fermerSansAccepter')}
            data-testid="banner-consent-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BannerConsent;
